import { useState } from 'react'
import { toast } from 'sonner'
import { Check } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Spinner } from '@/components/ui/shadcn-io/spinner'
import { IntelligenceBadge } from '@/components/ui/intelligence'
import AITermsDocument from './AITermsDocument'

/**
 * The agreement an agent accepts before the AI drafts anything for them. The
 * terms themselves are the document; this adds the accepting: read it,
 * confirm, and it is recorded against the account with its version.
 */
interface Props {
    open: boolean
    onOpenChange: (open: boolean) => void
    accepted: boolean
    onAccept: () => Promise<unknown>
    /** Nothing else can happen until it is accepted. */
    required?: boolean
}

export default function AITermsDialog({ open, onOpenChange, accepted, onAccept, required }: Props) {
    const [saving, setSaving] = useState(false)
    const [ticked, setTicked] = useState(false)

    const accept = async () => {
        setSaving(true)
        try {
            await onAccept()
            onOpenChange(false)
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'That could not be saved. Please try again.')
        } finally {
            setSaving(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={(next) => (!required || accepted) && onOpenChange(next)}>
            <DialogContent
                showCloseButton={!required || accepted}
                onEscapeKeyDown={(e) => required && !accepted && e.preventDefault()}
                className='flex max-h-[90dvh] w-[calc(100%-1rem)] max-w-2xl flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl'>
                <div className='border-b px-6 pb-4 pt-5'>
                    <IntelligenceBadge />
                    <DialogTitle className='mt-3 text-base'>Before you use Xpillar Intelligence</DialogTitle>
                    <DialogDescription className='text-sm'>
                        The AI drafts listings from your documents and can act on your behalf. Please read these terms before you continue.
                    </DialogDescription>
                </div>

                <div className='min-h-0 flex-1 overflow-y-auto px-6 py-5'>
                    <AITermsDocument />
                </div>

                <div className='border-t px-6 py-3'>
                    {accepted ? (
                        <div className='flex flex-wrap items-center justify-between gap-3'>
                            <span className='inline-flex items-center gap-1 text-xs text-green-700'>
                                <Check className='h-3 w-3' /> You have accepted these terms
                            </span>
                            <Button variant='outline' onClick={() => onOpenChange(false)}>
                                Close
                            </Button>
                        </div>
                    ) : (
                        <div className='flex flex-wrap items-center justify-between gap-x-4 gap-y-3'>
                            <label className='flex min-w-0 flex-1 cursor-pointer items-start gap-2 text-sm'>
                                <Checkbox checked={ticked} onCheckedChange={(next) => setTicked(Boolean(next))} className='mt-0.5' />
                                <span>I have read these terms and accept them on behalf of my listings.</span>
                            </label>
                            <div className='ml-auto flex shrink-0 gap-2'>
                                {(!required || accepted) && (
                                    <Button variant='outline' onClick={() => onOpenChange(false)}>
                                        Close
                                    </Button>
                                )}
                                <Button disabled={!ticked || saving} onClick={accept}>
                                    {saving ? <Spinner variant='circle' className='h-4 w-4' /> : 'Accept and continue'}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}
